/**
 * The bench orchestrator (BENCH-V2-SPEC §11 to §13, BENCH-UX-SPEC §1 to §5): key entry, the local pass, the cost dialog, the
 * iterative loop (Standard) or the round-major batches (Economy), the budget pause, the job queue with resume, and the lens.
 * Main thread, per SD-2: it holds the key and makes every provider call; measurement, planning, chunking and Tiers B and C
 * run in the bench worker (`src/worker/bench.ts`) behind `BenchClient`. A job runs exactly the plan the customer confirmed
 * (the worker keeps the prepared run between the estimate and the start, spec §15 item 6).
 */
import { effect } from '@preact/signals';
import {
	type DocumentBrief,
	buildBrief,
	estimateBriefTokens,
	isEmptyBrief,
} from '../../engine/bench/brief.ts';
import { defaultVersions, estimateRunCost } from '../../engine/bench/cost.ts';
import type { Severity } from '../../engine/bench/failure-report.ts';
import {
	GUARDRAILS_DEFAULT,
	GUARDRAILS_MAX_VERSIONS,
	GUARDRAILS_OFFERED,
} from '../../engine/bench/guardrails-flags.ts';
import { DEFAULT_KEEP, type KeepTier } from '../../engine/bench/keep.ts';
import {
	buildLens2,
	keptBestIds,
	keptIdsBySeverity,
	keptLenientIds,
	revertedText,
} from '../../engine/bench/lens2.ts';
import { DEFAULT_LOCAL_OPTIONS } from '../../engine/bench/local-pass.ts';
import type { RunReport } from '../../engine/bench/loop2.ts';
import type { Provider } from '../../engine/bench/types.ts';
import type { ChannelId } from '../../engine/channels/types.ts';
import type { BenchTask } from '../../shared/bench-protocol.ts';
import type { AllowedModel, Effort } from '../../shared/models.ts';
import type { SourceKind } from '../../shared/viewmodel.ts';
import { forgetKey, loadRememberedKey, rememberKey as persistKey } from '../prefs.ts';
import { localPassPriorMs, preparePriorMs } from '../rates.ts';
import { briefFn, completeFn, judgeFn } from '../rewrite/adapters.ts';
import { BUDGET_FACTOR, createBudgetGuard, limitFor, nextLimit } from '../rewrite/budget.ts';
import { createLimiter, limitProvider } from '../rewrite/limiter.ts';
import { createAnthropicProvider } from '../rewrite/provider-anthropic.ts';
import { createBatchClient } from '../rewrite/provider-batch.ts';
import { navigate } from '../router.ts';
import { addNotice, result as analysisResult } from '../state.ts';
import { MESSAGES, VERSIONS } from '../strings-bench.ts';
import { WORKING } from '../strings.ts';
import { beginWork, endWork, updateWork, withWork, work } from '../work.ts';
import type { BenchClient, Prepared } from './bench-client.ts';
import { createBenchClient } from './bench-worker-link.ts';
import {
	askPending,
	bootChain,
	chainReady,
	latest,
	latestOnScreen,
	origin,
	reviseLatestStep,
	rootOf,
	stepFinished,
} from './chain-control.ts';
import { baseOf, makerOfJob } from './chain.ts';
import type { VersionRef } from './chain.ts';
import { type EconomyProgress, runEconomy } from './economy.ts';
import { type BenchJob, type StopInfo, canResume, newJob } from './job.ts';
import { describeFault, describeNothing, infoMessage, jobStopped } from './messages.ts';
import { showOverview } from './overview-control.ts';
import {
	applyProgress,
	finalLine,
	noteEvent,
	noteFinished,
	noteSlowdown,
	roundLine,
	startLive,
} from './run-live.ts';
import { acquireRunLock, heldJobIds, watchRun } from './run-watch.ts';
import {
	apiKey,
	budgetDialogOpen,
	budgetState,
	chain,
	concurrency,
	costDialogOpen,
	costEstimate,
	economy,
	economyRound,
	effort,
	exportAppendix,
	guardrails,
	jobs,
	jobsElsewhere,
	keep,
	keyDialogOpen,
	keyRemembered,
	lastReport,
	lensJobId,
	lensOpen,
	lens as lensSignal,
	liveJobId,
	localEffectLines,
	localLog,
	localOptions,
	localResultText,
	model,
	parallelState,
	pendingRequest,
	revertedUnits,
	runLive,
	runMessage,
	runProgress,
	runSummary,
	runSummaryOpen,
	severityFilter,
	stage,
	versions,
	viewing,
} from './state.ts';
import type { RewriteScope, RunRequest } from './state.ts';
import {
	getBrief,
	getJob,
	listJobsDetailed,
	newJobId,
	putBrief,
	putJob,
	removeJob,
} from './store.ts';

/* ---------------- key ---------------- */

export function bootKey(): void {
	const k = loadRememberedKey();
	if (k) {
		apiKey.value = k;
		keyRemembered.value = true;
	}
}

export function openKeyDialog(): void {
	keyDialogOpen.value = true;
}

export function closeKeyDialog(): void {
	keyDialogOpen.value = false;
}

export function saveKey(key: string, remember: boolean): void {
	apiKey.value = key;
	keyRemembered.value = remember;
	if (remember) persistKey(key);
	else forgetKey();
	keyDialogOpen.value = false;
}

export function forgetKeyAction(): void {
	apiKey.value = null;
	keyRemembered.value = false;
	forgetKey();
	keyDialogOpen.value = false;
}

/** The status bar's rewrite item opens the status summary (UD-20). */
export function openRunSummary(): void {
	runSummaryOpen.value = true;
}

/** The job the summary describes: the running one, else the one of the last summary. */
export function summaryJob(): BenchJob | undefined {
	const id = activeJob?.id ?? runSummary.value?.jobId;
	return id ? jobs.value.find((j) => j.id === id) : undefined;
}

/* ---------------- local pass ---------------- */

/** The saved versions wait for the customer's answer (UD-28): no action starts until it is given. */
function refusedForAsk(): boolean {
	if (!askPending()) return false;
	runMessage.value = infoMessage(VERSIONS.ask.first);
	stage.value = 'error';
	return true;
}

export async function runLocalPass(): Promise<void> {
	if (refusedForAsk()) return;
	await chainReady();
	// the local pass works on the latest version (UD-27); its result is the next version
	const from = latest();
	if (!from) return;
	stage.value = 'local-running';
	runMessage.value = null;
	// two analyses and the pass run in the bench worker (UD-12): the page stays free and the stage and fraction update
	// the indicator; a prior-only estimate until the fraction can carry one (UD-11)
	try {
		const opts = { ...DEFAULT_LOCAL_OPTIONS, ...localOptions.value };
		const { text, messages, log } = await withWork(
			'local-pass',
			WORKING.ops.localPass,
			{ priorMs: localPassPriorMs(from.words), detail: WORKING.localStages.analysing },
			() =>
				client().localPass(from.text, opts, (st, fraction) =>
					updateWork('local-pass', { fraction, detail: WORKING.localStages[st] })
				)
		);
		localResultText.value = text;
		localEffectLines.value = messages;
		localLog.value = log;
		const changes = log.reduce((n, [, c]) => n + c, 0);
		const added = await stepFinished({ text, made: { kind: 'local', changes }, base: from.ref });
		if (!added) addNotice(VERSIONS.localOutside);
		stage.value = 'local-done';
	} catch (e) {
		runMessage.value = describeFault(e, 'local');
		stage.value = 'error';
	}
}

/* ---------------- deep rewrite entry points ---------------- */

export function startRewrite(scope: RewriteScope): void {
	if (refusedForAsk()) return;
	// a stretch or a selection names paragraphs on screen: they must be the latest version's (UD-27)
	if ((scope.kind === 'hotspot' || scope.kind === 'selection') && !latestOnScreen()) {
		runMessage.value = infoMessage(VERSIONS.notLatest);
		stage.value = 'error';
		return;
	}
	const req = toRequest(scope);
	if (!apiKey.value) {
		pendingRequest.value = req;
		openKeyDialog();
		return;
	}
	void prepareAndConfirm(req);
}

function toRequest(scope: RewriteScope): RunRequest {
	switch (scope.kind) {
		case 'flagged':
		case 'document':
		case 'selection':
			return { scope, mode: 'deep' };
		case 'translation':
			return { scope, mode: 'repair', only: ['MT-01' as ChannelId, 'TR-01' as ChannelId] };
		case 'channel':
			return { scope, mode: 'deep', only: [scope.id] };
		case 'hotspot':
			return { scope, mode: 'deep', only: [scope.channel] };
	}
}

/** After the key dialog closes, continue the pending request or job (if any). */
export function keySubmitted(): void {
	const req = pendingRequest.value;
	pendingRequest.value = null;
	if (req && apiKey.value) void prepareAndConfirm(req);
	else if (pendingResume && apiKey.value) {
		const job = pendingResume;
		pendingResume = null;
		void resumeJob(job);
	}
}

/* ---------------- the worker ---------------- */

let benchClient: BenchClient | null = null;
function client(): BenchClient {
	if (!benchClient) benchClient = createBenchClient();
	return benchClient;
}

function resetClient(): void {
	benchClient?.terminate();
	benchClient = null;
}

const taskOf = (req: Pick<RunRequest, 'scope' | 'mode' | 'only'>, text: string): BenchTask => ({
	text,
	scope: req.scope,
	mode: req.mode,
	...(req.only ? { only: req.only } : {}),
});

/* ---------------- estimate and confirm ---------------- */

interface Confirmed {
	req: RunRequest;
	task: BenchTask;
	prepared: Prepared;
	/** Words of the document, and whether its brief is already stored (the dialog prices the brief's calls when it is not). */
	words: number;
	briefCached: boolean;
	/** The version the task was made from: the latest when the customer asked (UD-27). */
	base: VersionRef;
}
let confirmed: Confirmed | null = null;
/** The customer chose the versions per passage in the dialog: a change of model no longer resets it to the model's default. */
let versionsChosen = false;

function estimateOf(c: Confirmed) {
	return estimateRunCost(c.prepared.shapes, {
		model: model.value,
		effort: effort.value,
		economy: economy.value,
		versions: versions.value,
		...(guardrailsOn() ? { guardrails: true } : {}),
		briefWords: c.briefCached ? 0 : c.words,
		briefTokens: estimateBriefTokens(c.words),
	});
}

async function prepareAndConfirm(req: RunRequest): Promise<void> {
	if (refusedForAsk()) return;
	await chainReady();
	const vm = analysisResult.value;
	const key = apiKey.value;
	// every action works on the latest version (UD-27)
	const from = latest();
	if (!vm || !key || !from) return;
	stage.value = 'estimating';
	runMessage.value = null;
	try {
		const task = taskOf(req, from.text);
		const prepared = await withWork(
			'estimating',
			WORKING.ops.estimating,
			{ priorMs: preparePriorMs(from.words) },
			() => client().prepare(task, true)
		);
		if (prepared.units === 0) {
			// an outcome, not a fault: say why nothing in this scope can be rewritten (UD-32)
			runMessage.value = prepared.nothing
				? describeNothing(prepared.nothing, { latestN: from.ref.n, viewedN: viewing.peek() })
				: infoMessage(MESSAGES.nothing.unknown);
			stage.value = 'idle';
			return;
		}
		const briefCached = (await getBrief(rootOf(vm))) !== undefined;
		confirmed = { req, task, prepared, words: from.words, briefCached, base: from.ref };
		versionsChosen = false;
		versions.value = defaultVersions(model.value, from.words);
		keep.value = DEFAULT_KEEP;
		guardrails.value = GUARDRAILS_OFFERED && GUARDRAILS_DEFAULT;
		costEstimate.value = estimateOf(confirmed);
		stage.value = 'confirm-cost';
		costDialogOpen.value = true;
	} catch (e) {
		runMessage.value = describeFault(e, 'estimate');
		stage.value = 'error';
	}
}

/** The cost dialog's effort control: the estimate is recomputed from the planned shapes (no new plan). */
export function setEffort(e: Effort): void {
	effort.value = e;
	if (confirmed) costEstimate.value = estimateOf(confirmed);
}

/** The cost dialog's "requests at once" (ED-86): it bounds the calls in flight and does not change the estimate. */
export function setConcurrency(n: number): void {
	concurrency.value = n;
}

/** The cost dialog's model control (ED-85): same recomputation; the job records the model it was started with. */
export function setModel(m: AllowedModel): void {
	model.value = m;
	if (confirmed && !versionsChosen) versions.value = defaultVersions(m, confirmed.words);
	if (confirmed) costEstimate.value = estimateOf(confirmed);
}

/** The cost dialog's keep standard (BENCH-V3-PLAN §7.1): applied at the end of the run; it does not change the cost. */
export function setKeep(tier: KeepTier): void {
	keep.value = tier;
}

/** The editorial guardrails are on for this run: the signal, and only while the option is offered (ED-191). */
function guardrailsOn(): boolean {
	return GUARDRAILS_OFFERED && guardrails.value;
}

/**
 * The cost dialog's editorial guardrails (DESIGN ED-191, UD-41): the rules go into every request, and the versions are capped at
 * what the guardrails allow, so the estimate is recomputed.
 */
export function setGuardrails(on: boolean): void {
	guardrails.value = GUARDRAILS_OFFERED && on;
	if (confirmed && costDialogOpen.value) costEstimate.value = estimateOf(confirmed);
}

/** The cost dialog's "versions per passage" (BENCH-V3-PLAN §6): more output per request, fewer retries wanted; priced in the estimate. */
export function setVersions(n: number): void {
	versionsChosen = true;
	versions.value = n;
	if (confirmed) costEstimate.value = estimateOf(confirmed);
}

export function setEconomy(on: boolean): void {
	economy.value = on;
	if (confirmed && costDialogOpen.value) costEstimate.value = estimateOf(confirmed);
}

/**
 * Cancel, or the dialog's own close event after "Start" (which arrives later): only a dialog still waiting for its answer is a
 * cancel; after "Start" the stage has moved on and the prepared worker must stay.
 */
export function cancelCostDialog(): void {
	costDialogOpen.value = false;
	if (stage.value !== 'confirm-cost') return;
	confirmed = null;
	resetClient();
	stage.value = 'idle';
}

export async function confirmCost(): Promise<void> {
	const c = confirmed;
	// who the job is about is the upload (version 0), whichever version is on screen
	const vm = origin() ?? analysisResult.value;
	if (!c || !vm) return;
	stage.value = 'running';
	costDialogOpen.value = false;
	confirmed = null;
	const est = estimateOf(c);
	const job = newJob({
		id: newJobId(),
		// the document (version 0), and the version the run starts from (UD-27)
		docHash: rootOf(vm),
		base: c.base,
		scope: c.req.scope,
		mode: c.req.mode,
		...(c.req.only ? { only: c.req.only } : {}),
		economy: economy.value,
		model: model.value,
		effort: effort.value,
		// Standard runs use the parallel scheduler (ED-86); Economy batches its rounds
		...(economy.value ? {} : { concurrency: concurrency.value }),
		// the guardrails ask for at most the versions their strategies allow
		versions: guardrailsOn() ? Math.min(versions.value, GUARDRAILS_MAX_VERSIONS) : versions.value,
		keep: keep.value,
		guardrails: guardrailsOn(),
		// who the job is about (UD-21 jobs list), and the working text its "before" is measured on (UD-22 overview)
		docName: vm.meta.name,
		sourceKind: vm.meta.sourceKind,
		words: c.words,
		baseText: c.prepared.text,
		totalChunks: c.prepared.chunks,
		limitUsd: limitFor(est.expectedUsd),
	});
	await putJob(job);
	await refreshJobs();
	// the brief is made from the working text the plan was made on, before the first request
	const briefText = c.prepared.text;
	if (job.economy) {
		await executeEconomy(job, undefined, briefText);
		return;
	}
	// a Standard run holds its job's lock for as long as it runs (UD-38): another tab then sees it running, not paused
	const lock = await acquireRunLock(job.id);
	try {
		await executeStandard(job, undefined, briefText);
	} finally {
		lock?.release();
	}
}

/* ---------------- a running job ---------------- */

let activeJob: { id: string; controller: AbortController; runId?: number } | null = null;
/** A job paused by the budget guard, kept so "Continue with a higher limit" can carry on from its last chunk or round. */
let pausedJob: BenchJob | null = null;
/** A job the customer asked to resume before a key was entered. */
let pendingResume: BenchJob | null = null;

const errorName = (e: unknown): string => (e instanceof Error ? e.name : '');

function beginRun(job: BenchJob, controller: AbortController): void {
	activeJob = { id: job.id, controller };
	liveJobId.value = job.id;
	pausedJob = null;
	stage.value = 'running';
	runMessage.value = null;
	runProgress.value = { done: job.doneChunks, total: job.totalChunks };
	economyRound.value = null;
	economyPhase = '';
	// the run's indicator: Standard reports chunks (a fraction, so an estimate once enough have run); Economy replaces
	// this with its own steps (`economyWork`)
	const f0 = job.totalChunks > 0 ? job.doneChunks / job.totalChunks : 0;
	runSummary.value = {
		jobId: job.id,
		startedAt: performance.now(),
		tally: { accepted: 0, partial: 0, failed: 0, untouched: 0 },
		last: null,
	};
	runLive.value = startLive(job.id, job.doneChunks, job.totalChunks, Date.now());
	beginWork('rewrite', WORKING.ops.rewriting, {
		fraction: f0,
		startFraction: f0,
		// the parallel path works in rounds, so "N of M stretches" would stand still for a whole round (UD-20, ED-86)
		detail:
			job.concurrency !== undefined && !job.economy
				? roundLine(null, null)
				: WORKING.stretches(String(job.doneChunks), String(job.totalChunks)),
		onOpen: openRunSummary,
	});
}

/** The parallel path's status line, from the live record and the limiter's count of calls in flight (live between messages). */
function paintRoundLine(): void {
	const item = work.peek().find((w) => w.id === 'rewrite');
	if (!item || item.detail === null) return;
	const detail = roundLine(runLive.peek(), parallelState.peek()?.inFlight ?? null);
	if (item.detail !== detail) updateWork('rewrite', { detail });
}

/** Economy's steps (a round's requests going out, the wait, the answers being checked) as the status bar's operation. */
let economyPhase = '';
function economyWork(info: EconomyProgress): void {
	const phase = info.phase ?? (info.waiting ? 'waiting' : 'submitting');
	const key = `${info.round}/${phase}`;
	const round = String(info.round);
	const label =
		phase === 'waiting'
			? WORKING.ops.economyWaiting(round)
			: phase === 'applying'
				? WORKING.ops.economyApplying(round)
				: WORKING.ops.economySubmitting(round);
	if (key !== economyPhase) {
		economyPhase = key;
		// a new step is a new operation: its estimate starts from its own clock
		beginWork('rewrite', label, {
			...(phase === 'waiting' ? { hint: WORKING.batchTypical } : {}),
			onOpen: openRunSummary,
		});
	}
	if (phase === 'waiting' && info.total !== undefined && info.answered !== undefined)
		updateWork('rewrite', {
			fraction: info.total > 0 ? info.answered / info.total : null,
			detail: WORKING.batchCounts(String(info.answered), String(info.total)),
		});
}

async function finishRun(job: BenchJob, report: RunReport): Promise<void> {
	// the run's output becomes the next version when the version it started from is still the latest (UD-27)
	const base = baseOf(job);
	const added = await stepFinished({
		text: report.text,
		made: makerOfJob(job),
		base,
	});
	if (!added) addNotice(VERSIONS.outside(String(base.n)));
	showReport(report, job.docHash, job.id);
	runProgress.value = { done: report.chunks.length, total: report.chunks.length };
	stage.value = 'done';
	budgetDialogOpen.value = false;
}

/**
 * The lens and the reverts of a finished run (a fresh run or a job opened for review). The reverts of the job that made the
 * latest version are the ones its version records; any other job's lens is read-only (P2).
 */
function showReport(report: RunReport, docHash: string, jobId: string): void {
	lastReport.value = report;
	lensJobId.value = jobId;
	severityFilter.value = 'all';
	exportAppendix.value = false;
	const last = chain.peek()?.versions.at(-1);
	const mine = last?.made.kind === 'job' && last.made.jobId === jobId;
	revertedUnits.value = new Set(mine ? (last?.reverted ?? []) : []);
	lensSignal.value = buildLens2(report, docHash);
}

/**
 * Standard: the iterative loop in the worker (§12.2). A v3 job (`concurrency` set) runs the parallel scheduler (ED-86)
 * behind an adaptive limiter that starts at 4 calls at once, never passes the job's choice, halves on a rate limit and
 * grows back after a run of successes; a job from before v3 runs the sequential path, one snapshot after every chunk.
 */
/**
 * The job's document brief (BENCH-V3-PLAN §8): the one it holds, else the stored brief of the document, else one made now
 * (one call per window of 30k words, Sonnet 5.5 at `medium`, behind the run's limiter and budget guard). A job that has a
 * snapshot or applied chunks is a resume of a run that did not have one: it gets none, so its requests stay as they were. The
 * brief is context, not a gate: a failure to make it is a run without notes. A budget stop or an abort is not swallowed.
 */
async function obtainBrief(
	job: BenchJob,
	provider: Provider,
	max: number,
	text: string | undefined
): Promise<DocumentBrief | undefined> {
	if (job.brief) return job.brief;
	if (job.snapshotJson !== undefined || job.doneChunks > 0 || !text) return undefined;
	const stored = await getBrief(job.docHash);
	if (stored) return stored;
	beginWork('brief', WORKING.ops.brief, { fraction: 0, onOpen: openRunSummary });
	try {
		const out = await buildBrief(text, briefFn(provider), {
			concurrency: Math.max(1, Math.min(4, max)),
			onWindow: (done, total) => updateWork('brief', { fraction: total > 0 ? done / total : null }),
		});
		if (out.windows === out.failed || isEmptyBrief(out.brief)) return undefined;
		await putBrief(job.docHash, out.brief);
		return out.brief;
	} catch (e) {
		const name = errorName(e);
		if (name === 'BudgetPausedError' || name === 'AbortError') throw e;
		return undefined;
	} finally {
		endWork('brief');
	}
}

async function executeStandard(
	start: BenchJob,
	snapshotJson?: string,
	briefText?: string
): Promise<void> {
	const key = apiKey.value;
	if (!key) return;
	const controller = new AbortController();
	beginRun(start, controller);
	let job = start;
	const parallel = job.concurrency !== undefined;
	const max = Math.max(1, job.concurrency ?? 1);
	const limiter = parallel
		? createLimiter({
				initial: Math.min(4, max),
				max,
				onChange: (s) => {
					const was = parallelState.peek();
					parallelState.value = s;
					// a fall in the limit is a slowdown: the service asked for a slower pace (or the output budget capped it)
					const live = runLive.peek();
					if (was && s.limit < was.limit && live)
						runLive.value = noteSlowdown(live, s.limit, Date.now());
					paintRoundLine();
				},
			})
		: null;
	const inner = createAnthropicProvider({
		apiKey: key,
		signal: controller.signal,
		...(limiter
			? {
					onRateLimited: (ms: number | null) => limiter.noteRateLimited(ms),
					onRateLimitHeaders: (h: {
						outputTokensRemaining: number | null;
						outputTokensReset: string | null;
					}) => {
						if (h.outputTokensRemaining === null || h.outputTokensReset === null) return;
						const resetAt = Date.parse(h.outputTokensReset);
						if (!Number.isNaN(resetAt))
							limiter.noteBudget({ outputTokensRemaining: h.outputTokensRemaining, resetAt });
					},
				}
			: {}),
	});
	const guard = createBudgetGuard(inner, 0, BUDGET_FACTOR, {
		spentUsd: job.spentUsd,
		limitUsd: job.limitUsd,
	});
	budgetState.value = guard.state();
	// the limiter outside the budget guard: a call waiting for a slot meets the budget when it starts, not when it queued
	const provider = limiter
		? limitProvider(guard.provider, limiter, controller.signal)
		: guard.provider;
	const c = client();
	c.use({
		complete: completeFn(provider, { model: job.model, effort: job.effort as Effort }),
		// the dialogue judge runs at its own effort (JUDGE_EFFORT, ED-96)
		judge: judgeFn(provider, { model: job.model }),
	});
	// what the page does while it runs (hidden, unloaded, offline): the reason a lost run stopped (UD-38)
	const watch = watchRun(job.id);
	const stopped = (reason: StopInfo['reason'], at?: string): StopInfo => ({
		reason,
		at: at ?? new Date().toISOString(),
	});
	const save = async (patch: Partial<BenchJob>): Promise<void> => {
		job = {
			...job,
			...patch,
			spentUsd: guard.state().spentUsd,
			updatedAt: new Date().toISOString(),
		};
		await putJob(job);
	};
	try {
		const brief = await obtainBrief(job, provider, max, briefText);
		if (brief && !job.brief) {
			const live = runLive.peek();
			if (live) runLive.value = noteEvent(live, { kind: 'brief' }, Date.now());
			await save({ brief });
		}
		const { id, end } = c.run({
			...(snapshotJson ? { snapshotJson } : {}),
			...(parallel ? { concurrency: max } : {}),
			...(brief ? { brief } : {}),
			...(job.versions !== undefined ? { versions: job.versions } : {}),
			...(job.keep !== undefined ? { keep: job.keep } : {}),
			...(job.guardrails ? { guardrails: true } : {}),
			onChunk: async ({ done, total, usage, snapshotJson: snap, tally, last }) => {
				const live = runLive.peek();
				if (live && last) runLive.value = noteFinished(live, last, Date.now());
				const prev = runSummary.value;
				runSummary.value = {
					jobId: job.id,
					startedAt: prev?.jobId === job.id ? prev.startedAt : performance.now(),
					tally,
					last: last ?? null,
				};
				// the parallel path reports its share of the work through `onProgress`
				if (!parallel) {
					runProgress.value = { done, total };
					updateWork('rewrite', {
						fraction: total > 0 ? done / total : null,
						detail: WORKING.stretches(String(done), String(total)),
					});
				}
				budgetState.value = guard.state();
				// the parallel path carries a snapshot every few seconds, not with every chunk
				if (snap === undefined && parallel) return;
				await save({
					...(snap !== undefined ? { snapshotJson: snap } : {}),
					doneChunks: done,
					totalChunks: total,
					usage,
					costUsd: guard.state().spentUsd,
				});
				void refreshJobs();
			},
			onProgress: async ({ progress, usage, snapshotJson: snap, chunks }) => {
				runProgress.value = { done: progress.doneChunks, total: progress.totalChunks };
				const live = runLive.peek();
				if (live) runLive.value = applyProgress(live, progress, chunks, Date.now());
				updateWork('rewrite', {
					// the final passes have no share of the work to extrapolate from: the spinner and the step, no estimate
					fraction: progress.phase === 'final' ? null : progress.fraction,
					detail:
						progress.phase === 'final'
							? finalLine(progress.final)
							: roundLine(runLive.peek(), parallelState.peek()?.inFlight ?? null),
				});
				budgetState.value = guard.state();
				if (snap === undefined) return;
				await save({
					snapshotJson: snap,
					doneChunks: progress.doneChunks,
					totalChunks: progress.totalChunks,
					usage,
					costUsd: guard.state().spentUsd,
				});
				void refreshJobs();
			},
		});
		activeJob = { id: job.id, controller, runId: id };
		liveJobId.value = job.id;
		const r = await end;
		if (r.kind === 'done') {
			const { snapshotJson: _drop, ...rest } = job;
			job = rest;
			await save({
				status: 'done',
				report: r.report,
				resultText: r.report.text,
				coverage: r.report.coverage,
				usage: r.report.usage,
				doneChunks: r.report.chunks.length,
			});
			await finishRun(job, r.report);
		} else {
			await save({ status: 'paused', snapshotJson: r.snapshotJson, stop: stopped('budget') });
			stage.value = 'idle';
		}
	} catch (e) {
		if (guard.state().paused || errorName(e) === 'BudgetPausedError') {
			await save({ status: 'paused', stop: stopped('budget') });
			pausedJob = job;
			budgetState.value = guard.state();
			budgetDialogOpen.value = true;
			stage.value = 'budget-paused';
		} else if (controller.signal.aborted) {
			await save({ status: 'cancelled' });
			stage.value = 'idle';
			runMessage.value = null;
		} else {
			const m = describeFault(e, 'run');
			runMessage.value = m;
			stage.value = 'error';
			// the job keeps the worded cause, so the jobs list says why it stopped; a network that was down says so
			const down = watch.state();
			await save({
				status: 'error',
				error: m.text,
				stop: down.state === 'offline' ? stopped('offline', down.since) : stopped('error'),
			});
		}
		resetClient();
	} finally {
		watch.stop();
		endWork('rewrite');
		parallelState.value = null;
		activeJob = null;
		liveJobId.value = null;
		await refreshJobs();
	}
}

/** Economy: the round-major driver. Every batch id goes into the record before it waits, so a reload can resume. */
async function executeEconomy(
	start: BenchJob,
	snapshotJson?: string,
	briefText?: string
): Promise<void> {
	const key = apiKey.value;
	if (!key) return;
	const controller = new AbortController();
	beginRun(start, controller);
	let job = start;
	const guard = createBudgetGuard(
		createAnthropicProvider({ apiKey: key, signal: controller.signal }),
		0,
		BUDGET_FACTOR,
		{ spentUsd: job.spentUsd, limitUsd: job.limitUsd }
	);
	budgetState.value = guard.state();
	const c = client();
	c.use({
		complete: completeFn(guard.provider, { model: job.model, effort: job.effort as Effort }),
		// the dialogue judge runs at its own effort (JUDGE_EFFORT, ED-96)
		judge: judgeFn(guard.provider, { model: job.model }),
	});
	const batches = createBatchClient({ apiKey: key, signal: controller.signal });
	// the cancel itself must outlive the abort that asks for it
	const canceller = createBatchClient({ apiKey: key });
	try {
		const brief = await obtainBrief(job, guard.provider, 4, briefText);
		if (brief && !job.brief) {
			const live = runLive.peek();
			if (live) runLive.value = noteEvent(live, { kind: 'brief' }, Date.now());
			job = { ...job, brief, spentUsd: guard.state().spentUsd };
			await putJob(job);
		}
		await c.open(snapshotJson, brief, job.versions, job.keep, job.guardrails);
		const end = await runEconomy(job, {
			client: c,
			batch: {
				submit: (r) => batches.submit(r),
				collect: (ids) => batches.collect(ids),
				cancel: async (ids) => {
					for (const id of ids) await canceller.cancel(id);
				},
			},
			save: async (j) => {
				job = { ...j, spentUsd: guard.state().spentUsd };
				runProgress.value = { done: j.doneChunks, total: j.totalChunks };
				budgetState.value = guard.state();
				await putJob(job);
				void refreshJobs();
			},
			model: job.model,
			effort: job.effort as Effort,
			signal: controller.signal,
			over: () => guard.state().paused || guard.state().spentUsd > guard.state().limitUsd,
			spend: (usd) => guard.add(usd),
			onProgress: (info) => {
				economyRound.value = { round: info.round, waiting: info.waiting };
				economyWork(info);
			},
		});
		if (end.kind === 'done') await finishRun(end.job, end.report);
		else if (end.kind === 'paused') {
			pausedJob = end.job;
			budgetState.value = guard.state();
			budgetDialogOpen.value = true;
			stage.value = 'budget-paused';
		} else {
			stage.value = 'idle';
		}
	} catch (e) {
		if (controller.signal.aborted) {
			stage.value = 'idle';
		} else {
			const m = describeFault(e, 'run');
			runMessage.value = m;
			stage.value = 'error';
			await putJob({ ...job, status: 'error', error: m.text });
		}
	} finally {
		endWork('rewrite');
		economyRound.value = null;
		activeJob = null;
		liveJobId.value = null;
		resetClient();
		await refreshJobs();
	}
}

export function cancelRun(): void {
	activeJob?.controller.abort();
	pausedJob = null;
}

/** "Continue with a higher limit": the limit is raised once, `spent` is carried, the run goes on from its last save. */
export function resumeBudget(newLimitUsd: number): void {
	budgetDialogOpen.value = false;
	const job = pausedJob;
	if (!job) return;
	void continueJob({ ...job, limitUsd: newLimitUsd, status: 'running' });
}

export function stopPausedJob(): void {
	budgetDialogOpen.value = false;
	pausedJob = null;
	stage.value = 'idle';
}

/** The limit a "continue" asks for: the current one, once over (spec §15 item 5). */
export const continueLimit = (limitUsd: number): number => nextLimit(limitUsd);

/* ---------------- resume and review ---------------- */

/** Runs a saved job from its snapshot: in the same worker if it is still prepared, else in a fresh one for the document. */
async function continueJob(from: BenchJob): Promise<void> {
	const vm = analysisResult.value;
	if (!vm) return;
	// the job runs again: the reason it last stopped is history (UD-38)
	const { stop: _stop, ...job } = from;
	// a Standard job is run by one page at a time: take its lock before anything is written, or leave it to the page that has it
	const lock = job.economy ? null : await acquireRunLock(job.id);
	if (!job.economy && !lock) {
		runMessage.value = infoMessage(MESSAGES.runningElsewhere);
		stage.value = 'idle';
		await refreshJobs();
		return;
	}
	// the run goes on from the version it started from: its working text, else (a job from before it was kept) the upload's
	const text = job.baseText ?? origin()?.text ?? vm.text;
	try {
		await putJob(job);
		const task: BenchTask = {
			text,
			scope: job.scope,
			mode: job.mode as BenchTask['mode'],
			...(job.only ? { only: job.only } : {}),
		};
		stage.value = 'running';
		runProgress.value = { done: job.doneChunks, total: job.totalChunks };
		// a fresh worker has nothing prepared; a plan is only needed when the run has no snapshot yet
		resetClient();
		const prepared = await withWork(
			'resuming',
			WORKING.ops.resuming,
			{ priorMs: preparePriorMs(job.words ?? vm.meta.words) },
			() => client().prepare(task, job.snapshotJson === undefined)
		);
		// a job from before the working text was kept takes it now, for its before/after overview (UD-22)
		const run = job.baseText ? job : { ...job, baseText: prepared.text };
		if (run !== job) await putJob(run);
		if (run.economy) await executeEconomy(run, run.snapshotJson, prepared.text);
		else await executeStandard(run, run.snapshotJson, prepared.text);
	} catch (e) {
		runMessage.value = describeFault(e, 'resume');
		stage.value = 'error';
	} finally {
		lock?.release();
	}
}

/** Resume a saved job (a reload, a pause). A `docHash` mismatch refuses (spec §12.3). */
export async function resumeJob(job: BenchJob): Promise<void> {
	// another tab is running it (UD-38): a second run of the same job would pay twice
	if (!job.economy && jobsElsewhere.peek().has(job.id)) {
		runMessage.value = infoMessage(MESSAGES.runningElsewhere);
		return;
	}
	const vm = analysisResult.value;
	const check = canResume(job, vm ? rootOf(vm) : undefined);
	if (!check.ok) {
		runMessage.value = { kind: 'error', text: MESSAGES.resumeRefused(check.reason) };
		stage.value = 'error';
		return;
	}
	if (!apiKey.value) {
		pendingResume = job;
		openKeyDialog();
		return;
	}
	if (job.spentUsd >= job.limitUsd && job.limitUsd > 0) {
		// the job stopped at its budget: the customer decides whether to go on
		pausedJob = job;
		budgetState.value = { spentUsd: job.spentUsd, limitUsd: job.limitUsd, paused: true };
		budgetDialogOpen.value = true;
		stage.value = 'budget-paused';
		return;
	}
	await continueJob({ ...job, status: 'running' });
}

/** Selecting a job opens it: a finished one for review, an unfinished one for resume (it is never deleted by selecting). */
export async function openJob(job: BenchJob): Promise<void> {
	// the job running in this page: its live status, never a second run of it
	if (job.id === liveJobId.peek()) {
		openRunSummary();
		return;
	}
	const vm = analysisResult.value;
	if (job.status === 'done' && job.report) {
		if (!vm || rootOf(vm) !== job.docHash) {
			runMessage.value = infoMessage(MESSAGES.reviewElsewhere);
			return;
		}
		showReport(job.report, job.docHash, job.id);
		lensOpen.value = true;
		navigate('#/read');
		// the full before/after overview over the lens (UD-22)
		void showOverview(job);
		return;
	}
	if (job.status === 'cancelled' || job.status === 'error') {
		const fresh = (await getJob(job.id)) ?? job;
		if (canResume(fresh, vm ? rootOf(vm) : undefined).ok) {
			await resumeJob(fresh);
			return;
		}
		runMessage.value = fresh.error
			? jobStopped(fresh.error)
			: { kind: 'error', text: MESSAGES.jobStatus(fresh.status) };
		return;
	}
	await resumeJob(job);
}

/* ---------------- reverts (§13.1) ---------------- */

/** Reverts change the latest version, and only when the lens is the latest step's (P2); the views then re-measure it. */
function applyReverts(ids: ReadonlySet<string>): void {
	const report = lastReport.value;
	const last = chain.peek()?.versions.at(-1);
	if (!report || last?.made.kind !== 'job' || last.made.jobId !== lensJobId.peek()) return;
	revertedUnits.value = ids;
	void reviseLatestStep(revertedText(report, ids), [...ids]);
}

/** Put the given units (kept failures, or any accepted ones) back to their original text. */
export function revertUnits(ids: readonly string[]): void {
	applyReverts(new Set([...revertedUnits.value, ...ids]));
}

export function revertChunk(key: string): void {
	const l = lensSignal.value;
	const chunk = l?.chunks.find((c) => c.key === key);
	if (chunk) revertUnits(chunk.units);
}

export function revertAllKept(): void {
	const report = lastReport.value;
	if (report) revertUnits(keptBestIds(report));
}

/** "Revert all lenient" (BENCH-V3-PLAN §7.3): the units the lenient pass kept go back to their originals. */
export function revertAllLenient(): void {
	const report = lastReport.value;
	if (report) revertUnits(keptLenientIds(report));
}

/** "Revert all style" and "Revert all signal-negative": the kept units (either standard) whose report has this severity. */
export function revertBySeverity(severity: Severity): void {
	const report = lastReport.value;
	if (report) revertUnits(keptIdsBySeverity(report, severity));
}

export function clearLens(): void {
	lensSignal.value = null;
	lastReport.value = null;
	lensJobId.value = null;
	revertedUnits.value = new Set();
	lensOpen.value = false;
}

/* ---------------- job queue ---------------- */

export async function refreshJobs(): Promise<void> {
	const list = await listJobsDetailed(liveJobId.peek());
	jobs.value = list.jobs;
	jobsElsewhere.value = list.elsewhere;
}

/** True when a Standard job's run lock is held by another page right now (read fresh, not from the list). */
async function isRunningElsewhere(job: BenchJob): Promise<boolean> {
	if (job.economy) return false;
	const held = await heldJobIds();
	return !!held && held.has(job.id);
}

/** Deleting a job is an explicit action (spec §13.4). */
export async function removeJobAction(job: BenchJob): Promise<void> {
	// a running job is cancelled first (the Cancel button), not removed under its run
	if (job.id === liveJobId.peek()) return;
	// nor under another tab's run
	if (jobsElsewhere.peek().has(job.id) || (await isRunningElsewhere(job))) return;
	await removeJob(job.id);
	await refreshJobs();
}

export async function bootJobs(): Promise<void> {
	// the open document's version chain (UD-27) follows every newly opened document
	bootChain();
	await refreshJobs();
	watchOtherTabs();
	// the lens belongs to its document: a new analysis clears it (spec §15 item 8)
	effect(() => {
		const vm = analysisResult.value;
		const l = lensSignal.peek();
		// a version of the same document keeps the lens (UD-27): the lens belongs to the document, version 0's hash
		if (l && (vm ? rootOf(vm) : undefined) !== l.docHash) clearLens();
		// an old job is named from the upload (version 0): a version on screen stands for its document (the chain may have
		// replaced the freshly opened upload by its latest version before this effect runs)
		const named = vm?.meta.version ? origin() : vm;
		if (named && !named.meta.version && rootOf(named) === (vm ? rootOf(vm) : ''))
			void nameOldJobs(named.meta);
	});
}

/** How often the list is read again while a job runs in another tab: its progress, and whether it has stopped (UD-38). */
const ELSEWHERE_POLL_MS = 8000;

/** The list follows other tabs: it is read again when this page comes back to the front, and while a job runs elsewhere. */
function watchOtherTabs(): void {
	if (typeof document === 'undefined') return;
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') void refreshJobs();
	});
	setInterval(() => {
		if (document.visibilityState === 'visible' && jobsElsewhere.peek().size > 0) void refreshJobs();
	}, ELSEWHERE_POLL_MS);
}

/**
 * A job from before names were kept (UD-21 jobs list) takes the open document's name, source and word count when that
 * document is the job's own (the same `docHash`), so the list names it from then on. A running job is left alone: its run
 * writes its own record.
 */
async function nameOldJobs(meta: {
	docHash: string;
	name: string;
	sourceKind: SourceKind;
	words: number;
}): Promise<void> {
	const old = jobs.peek().filter((j) => !j.docName && j.docHash === meta.docHash);
	let named = 0;
	for (const j of old) {
		const latest = await getJob(j.id);
		if (!latest || latest.docName || latest.status === 'running') continue;
		await putJob({ ...latest, docName: meta.name, sourceKind: meta.sourceKind, words: meta.words });
		named++;
	}
	if (named > 0) await refreshJobs();
}
